import type { ResolvedInstallationOrder } from "../types/installation";
import {
  findCatalogRowById,
  resolveTaxableFromSources,
} from "../utils/readResolveTaxable";
import { getResolvedDiscountDisplay } from "./getResolvedDiscountDisplay";
import {
  inferInstallationDiscountScopeFromResolved,
} from "./installationDiscountScope";

const round2 = (n: number) => Math.round(Number(n) * 100) / 100;

export type InstallationResolveFormikSync = {
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
};

export type ApplyInstallationResolveOptions = {
  /** Code sent on the resolve request — kept when backend omits discount_code in draft responses. */
  requestedDiscountCode?: string;
  /** UI fee rows sent on the resolve request — preserve taxable when backend echoes wrong flags. */
  existingFeeRows?: unknown[] | null;
  /** Vendor special-fee catalog — source of truth for taxable flags. */
  specialFeeCatalog?: unknown[] | null;
};

export type ApplyInstallationResolveResult = {
  feeSubtotal: number;
  installTax: number;
  feeTax: number;
  productTax: number;
  grandTotal: number;
  overallTax: number;
  mergedFees: Array<{
    id: string;
    name: string;
    amount: number;
    taxable: boolean;
    tax_percentage: number | null;
    tax_amount: number;
  }>;
};

/**
 * Write backend resolve response into Formik totals (replaces TaxEngine formik sync).
 */
export function applyInstallationResolveResponse(
  resolved: ResolvedInstallationOrder,
  formik: InstallationResolveFormikSync,
  fallbackInstallationSubtotal: number,
  options?: ApplyInstallationResolveOptions
): ApplyInstallationResolveResult {
  const feeRows = Array.isArray(resolved.specialfee) ? resolved.specialfee : [];
  const feeSubtotal = feeRows.reduce((s, f) => s + Number(f.specialfee_amount || 0), 0);
  const installTax = Number(resolved.installation_tax_amount || 0);
  const feeTax = feeRows.reduce((s, f) => s + Number(f.specialfee_tax_amount || 0), 0);
  const lineItems = Array.isArray(resolved.line_items) ? resolved.line_items : [];
  const productTax = lineItems.reduce(
    (s, li) => s + Number(li.item_tax_info?.total_product_tax_amount || 0),
    0
  );
  const overallTax = Number(resolved.over_all_tax_amount ?? installTax + feeTax + productTax);
  const installSub = Number(resolved.installation_cost ?? fallbackInstallationSubtotal);
  const grandTotal = Number(
    resolved.total_amount ??
      installSub + feeSubtotal + overallTax - Number(resolved.discount_amount || 0)
  );

  const mergedFees = feeRows.map((f) => {
    const id = String(f.specialfee_id || "");
    const existingRow = findCatalogRowById(options?.existingFeeRows, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    const catalogRow = findCatalogRowById(options?.specialFeeCatalog, id, [
      "id",
      "_id",
      "specialfee_id",
    ]);
    return {
      id,
      name: String(f.specialfee_name || ""),
      amount: Number(f.specialfee_amount || 0),
      taxable: resolveTaxableFromSources(
        [existingRow, catalogRow, f as Record<string, unknown>],
        ["taxable", "specialfee_taxable", "is_taxable"],
        true
      ),
      tax_percentage: f.specialfee_tax_percentage ?? null,
      tax_amount: Number(f.specialfee_tax_amount || 0),
      required: f.required === true,
    };
  });

  formik.setFieldValue("totals.installation_subtotal", installSub, false);
  formik.setFieldValue("totals.installation_tax_amount", installTax, false);
  formik.setFieldValue("totals.specialfee_subtotal", feeSubtotal, false);
  formik.setFieldValue("totals.specialfee_tax_amount", feeTax, false);
  const unitsCost = lineItems
    .filter((li) => li.is_new_purchase !== false)
    .reduce((s, li) => {
      const qty = Math.max(1, Number(li.quantity ?? 0));
      const changed = Number(li.changed_unit_price ?? li.unit_price ?? 0);
      return s + round2(changed * qty);
    }, 0);

  formik.setFieldValue("totals.grand_total", grandTotal, false);
  formik.setFieldValue("totals.units_cost", round2(unitsCost), false);
  formik.setFieldValue("totals.product_tax_amount", round2(productTax), false);
  formik.setFieldValue("specialfee", mergedFees, false);
  formik.setFieldValue("tax", overallTax, false);
  formik.setFieldValue("resolved_line_items", lineItems, false);
  formik.setFieldValue("tax_status", resolved.tax_status ?? "", false);

  if (resolved.subtotal_amount != null) {
    formik.setFieldValue("subtotal_amount", Number(resolved.subtotal_amount), false);
  }
  if (resolved.additional_cost != null) {
    formik.setFieldValue("additional_cost", Number(resolved.additional_cost), false);
  }
  if (resolved.installation_cost != null) {
    formik.setFieldValue("installation_cost", Number(resolved.installation_cost), false);
  }

  const requestedDiscountCode =
    options?.requestedDiscountCode !== undefined
      ? String(options.requestedDiscountCode).trim()
      : undefined;

  if (requestedDiscountCode === "") {
    formik.setFieldValue("discount_code", "", false);
    formik.setFieldValue("discount_amount", 0, false);
    formik.setFieldValue("product_discount_amount", 0, false);
    formik.setFieldValue("discount_applies_to", "installation", false);
  } else {
    const scope = inferInstallationDiscountScopeFromResolved(resolved);
    const lineCouponTotal = round2(
      lineItems
        .filter((li) => li.is_new_purchase !== false)
        .reduce(
          (s, li) =>
            s +
            Number(
              li.discount_on_total_quantity ??
                (li as { discount?: number }).discount ??
                0
            ),
          0
        )
    );
    const { code: discountCode, amount: discountAmount } = getResolvedDiscountDisplay(
      resolved,
      requestedDiscountCode
    );
    const displayAmount = round2(
      scope === "product"
        ? lineCouponTotal || discountAmount
        : discountAmount
    );

    formik.setFieldValue("discount_applies_to", scope, false);
    formik.setFieldValue("discount_amount", displayAmount, false);
    if (discountCode) {
      formik.setFieldValue("discount_code", discountCode, false);
    } else if (requestedDiscountCode) {
      formik.setFieldValue("discount_code", requestedDiscountCode, false);
    }
    formik.setFieldValue(
      "product_discount_amount",
      scope === "product" ? displayAmount : 0,
      false
    );
  }

  const installRate = resolved.installation_tax_percentage ?? "";
  const overallRate = resolved.over_all_tax_percentage ?? "";
  formik.setFieldValue("calc_install_rate_str", installRate, false);
  formik.setFieldValue("calc_overall_rate_str", overallRate, false);

  if (resolved.cart_id) {
    formik.setFieldValue("totals.cartId", String(resolved.cart_id), false);
  }

  return {
    feeSubtotal,
    installTax,
    feeTax,
    productTax,
    grandTotal,
    overallTax,
    mergedFees,
  };
}
